'use client';

import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import FrWeddingNav from '@/components/FrWeddingNav';
import { getTranslations } from '@/lib/i18n';

const stripePromise = getStripe();

interface PaymentFormProps {
  clientSecret: string;
  onSuccess: (pi: PaymentIntent) => void;
  onError: (msg: string) => void;
}

function PaymentForm({ clientSecret, onSuccess, onError }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const supabase = createClient();
  const t = getTranslations('fr');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/fr/gift` },
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message ?? 'Paiement échoué. Veuillez réessayer.');
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      const { error: confirmError } = await supabase.functions.invoke('confirm-payment', {
        body: { paymentIntentId: paymentIntent.id },
      });
      if (confirmError) console.error('Backend confirm error:', confirmError);
      onSuccess(paymentIntent);
    }
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-4 text-xs tracking-[0.3em] uppercase font-sans border border-faded-navy text-faded-navy hover:bg-faded-navy hover:text-buttermilk transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Heart size={14} strokeWidth={1.5} />
        {processing ? t.honeymoon.processing : t.honeymoon.completeContribution}
      </button>
    </form>
  );
}

export default function FrHoneymoonFundPage() {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [step, setStep] = useState<'form' | 'payment' | 'success'>('form');
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const supabase = createClient();
  const t = getTranslations('fr');

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount < 1) {
      setErrorMsg('Veuillez entrer un montant d\'au moins 1 £.');
      return;
    }
    setErrorMsg('');
    setLoading(true);

    const { data, error } = await supabase.functions.invoke<{ clientSecret?: string; error?: string }>('create-payment-intent', {
      body: {
        amount: Math.round(numAmount * 100),
        currency: 'gbp',
        contributorName: name || undefined,
        contributorEmail: email || undefined,
        message: message || undefined,
      },
    });

    setLoading(false);

    if (error || !data?.clientSecret) {
      setErrorMsg((data as { error?: string })?.error ?? error?.message ?? 'Une erreur s\'est produite. Veuillez réessayer.');
      return;
    }

    setClientSecret(data.clientSecret);
    setStep('payment');
  };

  return (
    <main className="bg-buttermilk min-h-screen">
      <FrWeddingNav />

      <section className="pt-40 pb-16 px-8 md:px-16 xl:px-24 text-center">
        <Heart size={28} strokeWidth={1} className="text-warm-sand mx-auto mb-8" />
        <div className="flex justify-center mb-6">
          <div className="relative max-w-sm w-full">
            <img src="/assets/images/IMG_0706-1777410023192.png" alt="George et Boadecea" className="w-full" />
            <span className="absolute bottom-2 right-2 text-white/70 font-sans" style={{ fontSize: '9px', letterSpacing: '0.02em' }}>
              La citrouille de Naoshima, oct. 24
            </span>
          </div>
        </div>
        <div className="h-px bg-border w-16 mx-auto mb-8" />
        <p className="font-sans text-sm text-stone leading-relaxed mb-4 max-w-lg mx-auto">{t.honeymoon.noGiftExpected}</p>
        <p className="font-sans text-sm text-stone leading-relaxed max-w-lg mx-auto">{t.honeymoon.contributeText}</p>
        <p className="font-sans text-sm text-stone leading-relaxed max-w-lg mx-auto mt-4">{t.honeymoon.arigato}</p>
      </section>

      <section className="pb-32 px-8 md:px-16 xl:px-24">
        <div className="max-w-md mx-auto">
          {step === 'success' && (
            <div className="border border-border p-10 bg-off-white text-center">
              <Heart size={24} strokeWidth={1} className="text-warm-sand mx-auto mb-6" />
              <p className="font-serif text-xl text-faded-navy mb-3">{t.honeymoon.thankYou}</p>
              <p className="font-sans text-sm text-stone leading-relaxed">{t.honeymoon.thankYouText}</p>
            </div>
          )}

          {step === 'payment' && clientSecret && (
            <div className="space-y-5">
              <button type="button" onClick={() => { setStep('form'); setErrorMsg(''); }} className="font-sans text-xs text-stone/60 hover:text-stone transition-colors duration-200 tracking-wide">
                {t.honeymoon.back}
              </button>
              <p className="font-sans text-xs tracking-[0.2em] uppercase text-stone mb-4">
                {t.honeymoon.securePayment} · £{amount}
              </p>
              {errorMsg && <p className="font-sans text-xs text-red-500 mb-2">{errorMsg}</p>}
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: { colorPrimary: '#3d4a5c', colorBackground: '#faf9f6', colorText: '#3d4a5c', fontFamily: 'inherit', borderRadius: '0px' },
                  },
                }}
              >
                <PaymentForm clientSecret={clientSecret} onSuccess={() => setStep('success')} onError={setErrorMsg} />
              </Elements>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleFormSubmit} className="space-y-5">
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">{t.honeymoon.amount}</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-sans text-sm text-stone">£</span>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    placeholder="Entrez un montant"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full border border-border bg-transparent pl-8 pr-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">{t.honeymoon.name}</label>
                <input
                  type="text"
                  placeholder="Votre nom"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy transition-colors"
                />
              </div>
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">{t.honeymoon.email}</label>
                <input
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy transition-colors"
                />
              </div>
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">{t.honeymoon.message}</label>
                <textarea
                  rows={3}
                  placeholder="Un message pour nous..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy transition-colors resize-none"
                />
              </div>
              {errorMsg && <p className="font-sans text-xs text-red-500">{errorMsg}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 text-xs tracking-[0.3em] uppercase font-sans border border-faded-navy text-faded-navy hover:bg-faded-navy hover:text-buttermilk transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Heart size={14} strokeWidth={1.5} />
                {loading ? t.honeymoon.processing : t.honeymoon.contribute}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
